import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Users, Wrench, GraduationCap, CalendarCheck, Paperclip, X, Loader2, ShieldCheck } from "lucide-react";
import { IMAGES, COMPANY, API_URL } from "../data/site";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";

const CULTURE = [
  {
    icon: Users,
    title: "Long-Term Crews",
    desc: "We don't have high turnover — we take pride in the long-term commitment of our people.",
  },
  {
    icon: Wrench,
    title: "Real Seat Time",
    desc: "Foremen operate all of our equipment. You'll learn every machine in the fleet, not just one.",
  },
  {
    icon: GraduationCap,
    title: "Simulator Training",
    desc: "One of the few in-house Ditch Witch directional boring simulators for building bore skills.",
  },
  {
    icon: CalendarCheck,
    title: "Steady Regional Work",
    desc: "Dynamic utility construction demand across California, Nevada & Arizona — from large bids to daily T&M jobs.",
  },
];

const inputCls =
  "w-full border border-white/15 bg-obsidian px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-blueprint";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_EXT = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".webp"];

export default function Careers() {
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", position: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [scanStatus, setScanStatus] = useState(null);
  const [attachment, setAttachment] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pollScan = async (uploadId) => {
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await axios.get(`${API_URL}/uploads/${uploadId}/status`);
        if (res.data.status === "clean") {
          setAttachment({ path: res.data.path, filename: res.data.filename });
          setScanStatus("clean");
          return;
        }
        if (res.data.status === "rejected") {
          setScanStatus("rejected");
          setFileError(res.data.detail);
          setFile(null);
          return;
        }
      } catch (err) {
        setScanStatus("rejected");
        setFileError(err?.response?.data?.detail || "Virus scan failed. Please try a different file.");
        setFile(null);
        return;
      }
    }
    setScanStatus("rejected");
    setFileError("Virus scan is taking longer than expected. Please try a different file.");
    setFile(null);
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ACCEPTED_EXT.includes(ext)) {
      setFileError("Unsupported file type. Use PDF, DOC, DOCX, JPG, PNG or WEBP.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError("File too large. Max size is 10MB.");
      e.target.value = "";
      return;
    }
    setFileError("");
    setFile(f);
    setAttachment(null);
    setScanStatus("scanning");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await axios.post(`${API_URL}/uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      pollScan(up.data.uploadId);
    } catch (err) {
      setScanStatus("rejected");
      setFileError(err?.response?.data?.detail || "Upload failed. Please try again.");
      setFile(null);
    }
  };

  const removeFile = () => {
    setFile(null);
    setFileError("");
    setScanStatus(null);
    setAttachment(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (scanStatus === "scanning") {
      toast.error("Please wait for the virus scan to finish.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/applications`, {
        ...form,
        attachmentPath: attachment?.path || null,
        attachmentName: attachment?.filename || null,
      });
      setDone(true);
      toast.success("Application received — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please call our office instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        testId="careers-hero"
        eyebrow="Careers // Join the Crew"
        lines={["Work that", "outlasts you."]}
        sub="Leo Tidwell Excavating is constantly expanding and would love to have you onboard. Infrastructure built today serves California, Nevada & Arizona for decades."
        image={IMAGES.careersHero}
      />

      <section data-testid="careers-culture" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Why LTEC</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
              Built by people who stay
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {CULTURE.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="h-full bg-obsidian p-6 sm:p-8">
                  <c.icon className="h-7 w-7 text-brand-light" />
                  <h3 className="mt-4 font-display text-2xl font-bold uppercase text-white">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="careers-apply" className="border-t border-white/10 bg-granite/50 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
            <Reveal>
              <Eyebrow>Quick Apply</Eyebrow>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
                Put your name in the yard
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-400">
                Tell us the position you're after — operator, locator, labor, fusion tech,
                driver — and what you've run. Our office reviews every application.
              </p>
              <div className="mt-8 border border-white/10 bg-obsidian p-6">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Our Office</p>
                <p className="mt-3 text-sm text-slate-300">{COMPANY.address}</p>
                <a
                  href={COMPANY.phoneHref}
                  data-testid="careers-phone"
                  className="mt-1 block text-sm text-blueprint hover:text-white"
                >
                  {COMPANY.phone}
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              {done ? (
                <div
                  data-testid="careers-success"
                  className="blueprint-grid flex h-full min-h-[24rem] flex-col items-center justify-center border border-blueprint/30 bg-obsidian p-10 text-center"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-blueprint">
                    Application Received
                  </p>
                  <p className="mt-4 font-display text-4xl font-extrabold uppercase text-white">
                    We'll be in touch.
                  </p>
                  <p className="mt-3 max-w-sm text-sm text-slate-400">
                    Thanks for your interest in LTEC. Our office reviews every application
                    directly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="careers-form" className="border border-white/10 bg-obsidian p-6 sm:p-8">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="app-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Full Name *
                      </label>
                      <input id="app-name" data-testid="careers-input-name" required value={form.fullName} onChange={set("fullName")} className={inputCls} placeholder="Jane Doe" />
                    </div>
                    <div>
                      <label htmlFor="app-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Email *
                      </label>
                      <input id="app-email" type="email" data-testid="careers-input-email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                    </div>
                    <div>
                      <label htmlFor="app-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Phone
                      </label>
                      <input id="app-phone" type="tel" data-testid="careers-input-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(805) 555-0100" />
                    </div>
                    <div>
                      <label htmlFor="app-position" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Applying Position *
                      </label>
                      <input id="app-position" data-testid="careers-input-position" required value={form.position} onChange={set("position")} className={inputCls} placeholder="HDD Operator, Laborer…" />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="app-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        Experience / How did you hear about us?
                      </label>
                      <textarea id="app-message" rows={4} data-testid="careers-input-message" value={form.message} onChange={set("message")} className={inputCls} placeholder="Equipment you've run, certifications, years in the field…" />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="app-file" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Attach Resume (optional)</label>
                      {!file ? (
                        <label
                          htmlFor="app-file"
                          data-testid="careers-file-dropzone"
                          className="flex cursor-pointer items-center gap-3 border border-dashed border-white/15 bg-obsidian px-4 py-3 text-sm text-slate-400 transition-colors hover:border-blueprint hover:text-white"
                        >
                          <Paperclip className="h-4 w-4 shrink-0 text-brand-light" />
                          PDF, DOC, DOCX, JPG, PNG or WEBP — up to 10MB
                        </label>
                      ) : (
                        <div
                          data-testid="careers-file-selected"
                          className="flex items-center justify-between gap-3 border border-white/15 bg-obsidian px-4 py-3 text-sm text-slate-200"
                        >
                          <span className="flex items-center gap-2 truncate">
                            {scanStatus === "scanning" ? (
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blueprint" />
                            ) : (
                              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" />
                            )}
                            <span className="truncate">{file.name}</span>
                            {scanStatus === "scanning" && (
                              <span data-testid="careers-file-scanning" className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Scanning…
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={removeFile}
                            data-testid="careers-file-remove-button"
                            aria-label="Remove resume"
                            className="shrink-0 text-slate-500 transition-colors hover:text-white"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      <input
                        id="app-file"
                        type="file"
                        data-testid="careers-file-input"
                        accept={ACCEPTED_EXT.join(",")}
                        onChange={onFileChange}
                        className="hidden"
                      />
                      {fileError && (
                        <p data-testid="careers-file-error" className="mt-2 text-xs text-red-400">{fileError}</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="submit"
                    data-testid="careers-submit-button"
                    disabled={submitting || scanStatus === "scanning"}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-light disabled:opacity-50"
                  >
                    {submitting ? "Submitting…" : scanStatus === "scanning" ? "Scanning attachment…" : "Submit Application"}
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
